import { Colors, Dialog } from "@blueprintjs/core";
import { FieldSet, IDataTableColumn, useGridState, useSearchApi } from "nsitools-react";
import * as React from "react";
import { Link } from "react-router-dom";

import { DogDysplasiaApi, DogDysplasiaGridDto, DogDysplasiaSearch } from "../../../../api";
import { CustomDataTable, EditButton } from "../../../../components";
import { useGlobalData } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { DogDysplasiaPanel } from "../../../../pages/dog/dog/item/health/DogDysplasiaPanel";

export interface IDrupaldDogDysplasiaListProps {
  dogId: number;
}

export const DrupaldDogDysplasiaList: React.FunctionComponent<IDrupaldDogDysplasiaListProps> = ({ dogId }) => {
  const { t } = useTl();
  const api = useApiService(DogDysplasiaApi);

  const [dogDysplasiaId, setdogDysplasiaId] = React.useState<number>(0);
  const [dialogOpened, setdialogOpened] = React.useState<boolean>(false);
  const { currentLanguage } = useGlobalData();
  const [sobjState, setsobjState] = React.useState<any>();

  const tableState = useGridState({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [10, 15, 25],
    pageSize: 15,
    sortKeys: { characteristic: "ASC" },
  });

  const searchFunction = React.useCallback(
    (sObj: DogDysplasiaSearch) => {
      setsobjState(
        DogDysplasiaSearch.fromJS({
          ...sObj,
          dogId: dogId,
          languageId: currentLanguage.languageId,
        })
      );
      return api.baseSearch(
        DogDysplasiaSearch.fromJS({
          ...sObj,
          dogId: dogId,
          languageId: currentLanguage.languageId,
        })
      );
    },
    [api, currentLanguage.languageId, dogId]
  );

  const { loading, search } = useSearchApi({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "action",
        alignment: "center",
        autoFitContent: true,
        render: (row: DogDysplasiaGridDto) => (
          <EditButton
            minimal={true}
            onClick={() => {
              setdogDysplasiaId(row.dogDysplasiaId);
              setdialogOpened(true);
            }}
          />
        ),
      },
      {
        header: () => t(ETLCodes.Characteristic),
        fieldName: "characteristic",
      },
      {
        header: () => t(ETLCodes.Value),
        fieldName: "healthValue",
        render: (row: DogDysplasiaGridDto) => (
          <div
            style={
              tableState.data
                .filter((e) => e.characteristicId === row.characteristicId && e !== row)
                .some((e) => row.createdOn > e.createdOn) ||
              tableState.data.filter((e) => e.characteristicId === row.characteristicId && e !== row).length === 0
                ? { fontWeight: "bold" }
                : {}
            }
          >
            {row.healthValue}
          </div>
        ),
      },
      {
        header: () => t(ETLCodes.RequestNumber),
        fieldName: "requestNumber",
        render: (row: DogDysplasiaGridDto) => (
          <Link target="_blank" to={`${ERoutes.dysplasiaRequest}/${row.dysplasiaRequestId}/detail/view`}>
            {row.requestNumber}
          </Link>
        ),
      },
    ],
    [t, tableState.data]
  );

  return (
    <>
      <FieldSet title={t(ETLCodes.Dysplasia)}>
        <CustomDataTable tableState={tableState} loading={loading} columns={columns} />
      </FieldSet>
      <Dialog
        title={t(ETLCodes.Characteristics)}
        isOpen={dialogOpened}
        onClose={() => setdialogOpened(false)}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        style={{ width: 850, height: 290, background: Colors.WHITE }}
      >
        <div style={{ marginRight: "2rem" }}>
          <DogDysplasiaPanel
            dogId={dogId}
            dogDysplasiaId={dogDysplasiaId}
            postSave={() => {
              setdialogOpened(false);
              search();
            }}
          />
        </div>
      </Dialog>
    </>
  );
};
